import TextField from '@mui/material/TextField'
import { ChangeEvent, useContext, useState } from 'react'
import { ILocation } from '../../../../../../../app/entities/Location'
import PrimaryCheckbox from '../../../../../global/components/checkbox/Checkbox'
import styles from './styles.module.css'
import { UserContext } from '../../../../../base/components/base-container/BaseContainer'
import hasPermission from '../../../../../global/utils/user/has-permission'
import { PERMISSIONS } from '../../../../../global/constants/permissions'
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import SecondaryButton from '../../../../../global/components/buttons/secondary-button/SecondaryButton'
import PrimaryButton from '../../../../../global/components/buttons/primary-button/PrimaryButton'
import { fetchData } from '../../../../../global/utils/fetch'
import { convertLocationToDaySettings } from '../../api'
import DaySettingsManagementModal from '../day-settings-management-modal/DaySettingsManagementModal'

export interface ILocationFormProps {
  setLocationFormInfo: Function
  locationFormInfo: Partial<ILocation>
}

export function LocationForm(props: ILocationFormProps) {
  const { user } = useContext(UserContext)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [isConverting, setIsConverting] = useState(false)
  const [isDaySettingsModalOpen, setIsDaySettingsModalOpen] = useState(false)
  
  const {
    name,
    addressStreet,
    addressCity,
    stateAbbr,
    zipCode,
    workDayFrom,
    workDayTo,
    productionMode,
    usingTolls,
    standbyQueueActive,
    useCapacityConstrainedRouting,
    lunchDuration,
    hubLegDriveTimeMax,
    nearSlotInterOrderDriveTimeMax,
    farSlotInterOrderDriveTimeMax,
    costCenter,
    daySettingsOnlyMode,
    objectId,
  } = props.locationFormInfo

  function updateLocationForm(e: ChangeEvent<HTMLInputElement>) {
    props.setLocationFormInfo({
      ...props.locationFormInfo,
      [e.target.name]: e.target.value,
    })
  }

  function updateProductionMode(e: ChangeEvent<HTMLInputElement>) {
    props.setLocationFormInfo({
      ...props.locationFormInfo,
      productionMode: e.target.checked,
    })
  }
  function updateUsingTolls(e: ChangeEvent<HTMLInputElement>) {
    props.setLocationFormInfo({
      ...props.locationFormInfo,
      usingTolls: e.target.checked,
    })
  }
  function updateStandbyQueueActive(e: ChangeEvent<HTMLInputElement>) {
    props.setLocationFormInfo({
      ...props.locationFormInfo,
      standbyQueueActive: e.target.checked,
    })
  }
  function updateUseCapacityConstrainedRouting(
    e: ChangeEvent<HTMLInputElement>,
  ) {
    props.setLocationFormInfo({
      ...props.locationFormInfo,
      useCapacityConstrainedRouting: e.target.checked,
    })
  }

  function handleOpenConfirmation() {
    setIsConfirmationOpen(true)
  }

  function handleOpenDaySettingsModal() {
    setIsDaySettingsModalOpen(true)
  }

  function handleCloseDaySettingsModal() {
    setIsDaySettingsModalOpen(false)
  }

  function handleCloseConfirmation() {
    setIsConfirmationOpen(false)
  }

  async function handleConvertLocation() {
    try {
      setIsConverting(true)
      
      // Call the conversion API endpoint
      const response = await fetchData(convertLocationToDaySettings(objectId as string))
      
      // Update the location form to reflect the change
      props.setLocationFormInfo({
        ...props.locationFormInfo,
        daySettingsOnlyMode: true
      })
      
      console.log('Location converted successfully:', response)
      
    } catch (error) {
      console.error('Error converting location:', error)
      // TODO: Add proper error handling with toast notification
    } finally {
      setIsConverting(false)
      setIsConfirmationOpen(false)
    }
  }

  // permissions variables
  const isLocationInfoEditDisabled = !hasPermission(user).edit(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.LOCATION_INFO.NAME,
  )
  const isLocationInfoViewable = hasPermission(user).view(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.LOCATION_INFO.NAME,
  )

  const isWorkdayInfoEditDisabled = !hasPermission(user).edit(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.WORKDAY_INFO.NAME,
  )
  const isWorkdayInfoViewable = hasPermission(user).view(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.WORKDAY_INFO.NAME,
  )

  const isRoutingInfoEditDisabled = !hasPermission(user).edit(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.ROUTING.NAME,
  )
  const isRoutingInfoViewable = hasPermission(user).view(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.ROUTING.NAME,
  )

  const isCostCenterInfoEditDisabled = !hasPermission(user).edit(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.COST_CENTER.NAME,
  )
  const isCostCenterInfoViewable = hasPermission(user).view(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.COST_CENTER.NAME,
  )

  const isProductionModeButtonDisabled = !hasPermission(user).edit(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.PRODUCTION_MODE_BUTTON.NAME,
  )
  const isProductionModeButtonViewable = hasPermission(user).view(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.PRODUCTION_MODE_BUTTON.NAME,
  )
  const isUsingTollsDisabled = !hasPermission(user).edit(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.USING_TOLLS_BUTTON.NAME,
  )
  const isUsingTollsViewable = hasPermission(user).view(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.USING_TOLLS_BUTTON.NAME,
  )

  const isStandbyQueueButtonDisabled = !hasPermission(user).edit(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.STANDBY_QUEUE_BUTTON.NAME,
  )
  const isStandbyQueueButtonViewable = hasPermission(user).view(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.STANDBY_QUEUE_BUTTON.NAME,
  )

  const isCapacityConstrainedRoutingButtonDisabled = !hasPermission(user).edit(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.CAPACITY_CONSTRAINED_ROUTING_BUTTON.NAME,
  )
  const isCapacityConstrainedRoutingButtonViewable = hasPermission(user).view(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.CAPACITY_CONSTRAINED_ROUTING_BUTTON.NAME,
  )

  return (
    <div className={styles.locationFormContainer}>
      {isLocationInfoViewable && (
        <div className={styles.locationFormFlexBox}>
          <div className={styles.locationFormTitle}>Location</div>
          <TextField
            onChange={updateLocationForm}
            name='name'
            value={name || ''}
            className={styles.wideTextField}
            label='Name'
            disabled={isLocationInfoEditDisabled}
          />
          <TextField
            onChange={updateLocationForm}
            name='addressStreet'
            value={addressStreet || ''}
            className={styles.wideTextField}
            label='Street'
            disabled={isLocationInfoEditDisabled}
          />
          <TextField
            onChange={updateLocationForm}
            name='addressCity'
            value={addressCity || ''}
            className={styles.wideTextField}
            label='City'
            disabled={isLocationInfoEditDisabled}
          />
          <div className={styles.doubleFieldContainer}>
            <TextField
              onChange={updateLocationForm}
              name='stateAbbr'
              value={stateAbbr || ''}
              className={styles.narrowTextField}
              label='State Abbr'
              disabled={isLocationInfoEditDisabled}
            />
            <TextField
              onChange={updateLocationForm}
              name='zipCode'
              value={zipCode || ''}
              className={styles.narrowTextField}
              label='Zip'
              disabled={isLocationInfoEditDisabled}
            />
          </div>
        </div>
      )}
      {isWorkdayInfoViewable && (
        <div className={styles.locationFormFlexBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div className={styles.workdayLabel}>Workday</div>
            {!daySettingsOnlyMode && objectId && !isLocationInfoEditDisabled && (
              <button
                onClick={handleOpenConfirmation}
                disabled={isConverting}
                style={{
                  all: 'unset',
                  fontFamily: 'Poppins',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '150%',
                  color: isConverting ? '#c2c2c2' : '#d70000',
                  cursor: isConverting ? 'not-allowed' : 'pointer',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isConverting) {
                    (e.target as HTMLButtonElement).style.textDecoration = 'underline'
                  }
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.textDecoration = 'none'
                }}
              >
                {isConverting ? 'Converting...' : 'Transition to Dynamic Scheduling'}
              </button>
            )}
            {daySettingsOnlyMode && objectId && !isLocationInfoEditDisabled && (
              <button
                onClick={handleOpenDaySettingsModal}
                style={{
                  all: 'unset',
                  fontFamily: 'Poppins',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '150%',
                  color: '#0066cc',
                  cursor: 'pointer',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.textDecoration = 'underline'
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.textDecoration = 'none'
                }}
              >
                Manage Technician Schedules
              </button>
            )}
          </div>
          {/* Hide working hours and lunch duration for day-settings-only locations */}
          {!daySettingsOnlyMode && (
            <>
              <div className={styles.doubleFieldContainer}>
                <TextField
                  onChange={updateLocationForm}
                  name='workDayFrom'
                  value={workDayFrom || ''}
                  className={styles.narrowTextField}
                  label='Workday From'
                  disabled={isWorkdayInfoEditDisabled}
                />
                <TextField
                  onChange={updateLocationForm}
                  name='workDayTo'
                  value={workDayTo || ''}
                  className={styles.narrowTextField}
                  label='Workday To'
                  disabled={isWorkdayInfoEditDisabled}
                />
              </div>
              <div className={styles.doubleFieldContainer}>
                <TextField
                  onChange={updateLocationForm}
                  name='lunchDuration'
                  value={lunchDuration || ''}
                  className={styles.narrowTextField}
                  label='Lunch Duration'
                  disabled={isWorkdayInfoEditDisabled}
                />
              </div>
            </>
          )}
        </div>
      )}
      {isRoutingInfoViewable && (
        <div className={styles.locationFormFlexBox}>
          <div className={styles.workdayLabel}>Routing</div>
          <div className={styles.workdayLabel}>Drive Time Variables</div>
          <div className={styles.doubleFieldContainer}>
            <TextField
              onChange={updateLocationForm}
              name='hubLegDriveTimeMax'
              value={hubLegDriveTimeMax || ''}
              className={styles.narrowTextField}
              label='Out and Back'
              disabled={isRoutingInfoEditDisabled}
            />
          </div>
          <div className={styles.doubleFieldContainer}>
            <TextField
              onChange={updateLocationForm}
              name='nearSlotInterOrderDriveTimeMax'
              value={nearSlotInterOrderDriveTimeMax || ''}
              className={styles.narrowTextField}
              label='1-day Job-to-Job'
              disabled={isRoutingInfoEditDisabled}
            />
            <TextField
              onChange={updateLocationForm}
              name='farSlotInterOrderDriveTimeMax'
              value={farSlotInterOrderDriveTimeMax || ''}
              className={styles.narrowTextField}
              label='14-day Job-to-Job'
              disabled={isRoutingInfoEditDisabled}
            />
          </div>
        </div>
      )}
      {isCostCenterInfoViewable && (
        <div className={styles.locationFormFlexBox}>
          <div className={styles.workdayLabel}>Cost Center</div>
          <TextField
            className={styles.narrowTextField}
            label='Cost Center'
            name='costCenter'
            onChange={updateLocationForm}
            type='number'
            value={costCenter || ''}
            disabled={isCostCenterInfoEditDisabled}
          />
        </div>
      )}
      <div className={styles.checkboxContainer}>
        {isProductionModeButtonViewable && (
          <PrimaryCheckbox
            checked={productionMode as boolean}
            name='productionMode'
            onChange={updateProductionMode}
            label='Production Mode'
            className={styles.locationCheckboxSmallText}
            disabled={isProductionModeButtonDisabled}
          />
        )}
        {isUsingTollsViewable && (
          <PrimaryCheckbox
            checked={usingTolls as boolean}
            name='usingTolls'
            onChange={updateUsingTolls}
            label='Using Tolls'
            className={styles.locationCheckboxSmallText}
            disabled={isUsingTollsDisabled}
          />
        )}
        {isStandbyQueueButtonViewable && (
          <PrimaryCheckbox
            checked={standbyQueueActive as boolean}
            name='standbyQueue'
            onChange={updateStandbyQueueActive}
            label='Standby Queue'
            className={styles.locationCheckboxSmallText}
            disabled={isStandbyQueueButtonDisabled}
          />
        )}
        {isCapacityConstrainedRoutingButtonViewable && (
          <PrimaryCheckbox
            checked={useCapacityConstrainedRouting as boolean}
            name='useCapacityConstrainedRouting'
            onChange={updateUseCapacityConstrainedRouting}
            label='Use Capacity Constrained Routing'
            className={styles.locationCheckboxSmallText}
            disabled={isCapacityConstrainedRoutingButtonDisabled}
          />
        )}
      </div>
      
      {/* Conversion Confirmation Modal */}
      <Dialog
        open={isConfirmationOpen}
        onClose={handleCloseConfirmation}
        aria-labelledby="conversion-dialog-title"
        aria-describedby="conversion-dialog-description"
      >
        <DialogTitle 
          id="conversion-dialog-title"
          style={{
            color: '#000000',
            fontWeight: 600,
            paddingBottom: 0,
          }}
        >
          🚨 Transition to Dynamic Scheduling 🚨
        </DialogTitle>
        <DialogContent style={{ paddingTop: '16px' }}>
          <DialogContentText id="conversion-dialog-description">
            <p style={{ margin: '0 0 16px 0' }}>
              Are you sure you want to transition this location to dynamic scheduling mode?
            </p>
            <p style={{ margin: '0 0 16px 0' }}>
              This action will:
            </p>
            <ul style={{ margin: '0 0 16px 0', paddingLeft: '20px' }}>
              <li>Convert all technician schedules to the new scheduling system</li>
              <li>Generate scheduling data for the next 180 days</li>
              <li>Cannot be undone</li>
            </ul>
            <p style={{ margin: '0' }}>
              This process may take a few moments to complete.
            </p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            buttonName="Cancel"
            onClick={handleCloseConfirmation}
            disabled={isConverting}
          />
          <PrimaryButton
            buttonName={isConverting ? "Converting..." : "Transition Location"}
            onClick={handleConvertLocation}
            disabled={isConverting}
          />
        </DialogActions>
      </Dialog>

      {/* DaySettings Management Modal */}
      <DaySettingsManagementModal
        isOpen={isDaySettingsModalOpen}
        onClose={handleCloseDaySettingsModal}
        locationObjectId={objectId || ''}
      />
    </div>
  )
}
